import React, { useState } from 'react';
import layout from "../../../Modules/layout.module.css";
import styles from "./PickList.module.css";

export type ISelectOption = {
  value: string;
  label: string;
  required: boolean;
};

interface MultiSelectProps {
  availableOptions: ISelectOption[];
  selectedOptions: ISelectOption[];
  onChange: (selectedOptions: ISelectOption[]) => void;
  placeholder?: string; // Optional placeholder text
  noOptionsMessage?: string;
  readonly?: boolean;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ availableOptions, selectedOptions, onChange, placeholder, noOptionsMessage, readonly }) => {

  // State to track the visibility of the custom dropdown
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);

  // Handle adding new items to selected options
  const handleAdd = (selectedValue: string) => {
    if (readonly) return;
    const optionToAdd = availableOptions.find(option => option.value === selectedValue);
    if (optionToAdd && !selectedOptions.some(option => option.value === optionToAdd.value)) {
      onChange([...selectedOptions, optionToAdd]);
      setIsDropdownVisible(false); // Hide the dropdown after selecting an option
    }
  };

  // Handle removing selected items
  const handleRemove = (option: ISelectOption, event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (readonly) return;
    event.stopPropagation(); // Prevent the container from toggling the dropdown
    onChange(selectedOptions.filter((x) => x.value !== option.value)); // Remove the item from selected options
  };

  // Toggle the visibility of the dropdown when the container is clicked
  const toggleDropdown = () => {
    if (readonly || availableOptions.length === 0) return;
    setIsDropdownVisible(prevState => !prevState); // Toggle dropdown visibility
  };

  return (
    <div className={styles.container}>
      <div className={styles.multiSelectContainer} onClick={toggleDropdown}>
        {/* Display placeholder text if no items are selected */}
        {selectedOptions.length === 0 ? (
          <p className={styles.placeholder}>
            {availableOptions.length > 0
              ? (placeholder || "Select options")
              : (noOptionsMessage || "No options available")}
          </p>
        ) : (
          selectedOptions.map((option, index) => (
            <div key={index} className={`${styles.selectedItem} ${styles.padding10}`}>
              <span>{option.label}</span>
              {!readonly && (
                <button 
                  className={styles.removeButton} 
                  onClick={(e) => handleRemove(option, e)}
                >
                  ×
                </button>
              )}
            </div>
          ))
        )}
      </div>

      {/* Custom dropdown to select an option */}
      {isDropdownVisible && !readonly && (
        <div className={styles.customDropdown}>
          <div className={styles.dropdownList}>
            {availableOptions
              .filter(option => !selectedOptions.some(selected => selected.value === option.value)) // Filter out already selected options
              .map((option) => (
                <div
                  key={option.value}
                  className={styles.dropdownOption}
                  onClick={() => handleAdd(option.value)} // Add option when clicked
                >
                  {option.label}
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
